import type { Match } from '@uefadigital/panenka';
import clsx from 'clsx';
import i18n from 'i18next';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMatchUnitByIdQuery } from 'api/fspApi';
import { MatchUnit } from 'components/MatchUnit';
import useIsDesktop from 'hooks/useIsDesktop';

interface MatchUnitDayData {
  params: {
    matchDayId?: Array<string>;
    teamId?: string;
  };
  isFixturesPage: boolean;
  hideMatchUnitDetail: boolean;
}

interface NormalizedMatchesData {
  [key: string]: Array<Match>;
}

export const MatchUnitDay = memo(({ params, isFixturesPage, hideMatchUnitDetail }: MatchUnitDayData) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const language = i18n.language.toUpperCase();

  const {
    data: matches = [],
    isError: matchesError,
    isLoading: matchesLoading,
    isFetching: matchesFetching,
  } = useGetMatchUnitByIdQuery(params, {
    skip: !params.matchDayId?.length && !params.teamId,
  });

  const normalizedMatchesObj = useMemo(
    () =>
      matches.toReversed().reduce((acc: NormalizedMatchesData, el) => {
        const start = new Date(el?.kickOffTime?.date ?? '');
        const date = start.toLocaleDateString(language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        const day = new Intl.DateTimeFormat(language, { weekday: 'long' }).format(start);
        const generalDate = `${day}, ${date}`;

        acc[generalDate] = generalDate in acc ? [...acc[generalDate], el] : [el];

        return acc;
      }, {}),
    [matches, language],
  );

  const Matches = useMemo(
    () =>
      ({ matches }: { matches: Array<Match> }) =>
        matches.map((el, key) => {
          const hasDivider = matches.length - 1 !== key;

          return (
            <MatchUnit
              match={el}
              key={el.id}
              isDesktop={isDesktop}
              hasDivider={hasDivider}
              isFixturesPage={isFixturesPage}
              hideMatchUnitDetail={hideMatchUnitDetail}
            />
          );
        }),
    [isDesktop, isFixturesPage, hideMatchUnitDetail],
  );

  if ((!params.matchDayId?.length && !params.teamId) || !matches?.length || matchesFetching || matchesLoading) {
    return <div />;
  }

  if (!matches.length || matchesError) {
    return (
      <div className="pk-text--center">
        <p>{t('fixtures_not_available_yet')}</p>
        <p>{t('dates_and_kick_off_time_subject')}.</p>
      </div>
    );
  }

  return (
    <div>
      {Object.entries(normalizedMatchesObj).map((day, i) => {
        return (
          <div key={i} className="pk-mb--xl2">
            <div>
              <p
                className={clsx(
                  'pk-mb--m',
                  'pk-font-size--l',
                  'pk-text--text-01',
                  'pk-font--base-bold',
                  isDesktop ? 'pk-line-height--md' : 'pk-line-height--sm',
                )}
              >
                {day[0]}
              </p>
              <div>
                <Matches matches={day[1]} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});
MatchUnitDay.displayName = 'MatchUnitDay';
